import type { PermissionContext } from '@ardoq/access-control';
import type { I18nLocale } from '@ardoq/i18n';
import type {
  DataSourceField,
  FilterExpression,
  WidgetDataSourceEntity,
} from '@ardoq/api-types';
import type {
  DashboardBuilderWidgetsProps,
  DashboardColor,
  DashboardErrorType,
  DashboardFiltersLinkCountDict,
  EditModeDashboardProps,
  WidgetWarning,
} from '@ardoq/dashboard';
import type { SelectOption } from '@ardoq/select';
import type { DashboardBuilder$State } from './streams/DashboardBuilder$';
import type { NewFeatureNotificationProps } from '@ardoq/snowflakes';
import type { ConditionSectionProps } from './sidebars/sections/ConditionSection';
import type { NewNavBarProps } from 'views/navbar/NavbarView';

export type Layout = {
  // This is a simplified type to match the type with the same name from the `react-grid-layout` library. Didn't want to add it as a dependency just for the type.
  x: number;
  y: number;
  h: number;
  w: number;
  i: string;
};

export enum LoadingState {
  LOADING = 1,
  SAVING,
  LOADING_ERROR,
  SAVING_ERROR,
  FETCH_DATA_SOURCES_ERROR,
}

export type CanEditDashboardGuardProps = {
  children: React.ReactNode;
  dashboardId?: string;
  permissionContext: PermissionContext;
  i18nLocale: I18nLocale;
};

export type ConflictType = 'chartFilter' | 'dashboardFilter';

export type PendingConditionSelection = {
  widgetId: string;
  columnKey: string;
  conflictType: ConflictType;
};

export type WidgetCondition = {
  columnKey: string;
  values: FilterExpression[];
};

export type DashboardLevelValidationErrors = Set<DashboardErrorType>;

export const SURVEY_VALID_INVALID_FIELD_NAME = 'ardoq.valid';

export type FieldSelectProps = {
  errorMessage?: string;
  isDisabled: boolean;
  isSortable: boolean;
  options: SelectOption<DataSourceField>[];
  selectedValues: SelectOption<DataSourceField>[];
  widgetId: string;
  warningMessage?: string;
};

export type DataSelectionProps = {
  fieldSelectProps: FieldSelectProps;
};

export type DashboardBuilderProps = DashboardBuilder$State & {
  dataSelectionProps?: DataSelectionProps;
  errorNotifications: string[];
  selectedDashboardId?: string;
  permissionContext: PermissionContext;
  newFeatureNotifications?: NewFeatureNotificationProps[];
  validationErrors: DashboardLevelValidationErrors;
  settings: { colors: Array<DashboardColor> };
  availableDataSources: WidgetDataSourceEntity[];
  selectedWidgetId: string | null;
  dashboardFiltersLinkCountDict?: DashboardFiltersLinkCountDict;
  isInLinkDashboardFiltersMode: boolean;
  conditionSectionProps?: ConditionSectionProps;
  hasChanges: boolean;
  navBarProps: NewNavBarProps;
  hasPendingConditionConflict: boolean;
  saveDashboard: VoidFunction;
  viewDashboard: VoidFunction;
  editModeDashboardProps: EditModeDashboardProps;
  widgetWarnings: WidgetWarning[];
  selectedWidget: DashboardBuilderWidgetsProps | undefined;
  translations: I18nLocale;
};
